import axios from 'axios';
import type { OrderItem } from '../types/apiTypes';
import {
  OrderPickScansClearedResponseSchema,
  OrderPickScansResponseSchema,
} from '../schemas/apiResponse.schemas';
import type { OrderPickScanLine } from '@/shared/utils/orderPickScan';
import {
  loadBundleBinScans,
  loadScannedItems,
  scanSessionBinStorageKey,
  scanSessionStorageKey,
  type BundleBinScanMap,
} from '../utils/scanSessionCache';
import { validateResponse } from './validateResponse';

export type { OrderPickScanLine };

export async function fetchOrderPickScans(orderId: string): Promise<OrderPickScanLine[]> {
  const response = await axios.get(`/api/orders/${encodeURIComponent(orderId)}/scans`);
  const parsed = validateResponse(OrderPickScansResponseSchema, response.data, 'fetchOrderPickScans');
  return parsed.scans;
}

export async function saveOrderPickScans(
  orderId: string,
  lines: readonly OrderPickScanLine[],
): Promise<OrderPickScanLine[]> {
  const response = await axios.post(`/api/orders/${encodeURIComponent(orderId)}/scans`, { lines });
  const parsed = validateResponse(OrderPickScansResponseSchema, response.data, 'saveOrderPickScans');
  return parsed.scans;
}

export async function clearOrderPickScans(orderId: string): Promise<void> {
  const response = await axios.delete(`/api/orders/${encodeURIComponent(orderId)}/scans`);
  validateResponse(OrderPickScansClearedResponseSchema, response.data, 'clearOrderPickScans');
}

export interface OrderPickScanOverlay {
  cleared: boolean;
  added: Map<string, OrderPickScanLine>;
  removed: Set<string>;
}

/** Remote events that arrived while a load was in flight stay applied. */
export function applyOrderPickScanOverlay(
  hydrated: { readonly items: readonly OrderItem[]; readonly bins: BundleBinScanMap },
  overlay: OrderPickScanOverlay,
): { items: OrderItem[]; bins: BundleBinScanMap } {
  const items = hydrated.items.map((item) => {
    if (overlay.added.has(item.id)) return { ...item, scanned: true };
    if (overlay.cleared || overlay.removed.has(item.id)) return { ...item, scanned: false };
    return item;
  });
  const bins: Record<string, { sku: string; storageIdPrefix: string | null }> = overlay.cleared
    ? {}
    : { ...hydrated.bins };
  for (const lineId of overlay.removed) {
    delete bins[lineId];
  }
  return {
    items,
    bins: { ...bins, ...binsFromOrderPickScans([...overlay.added.values()]) },
  };
}

export function binsFromOrderPickScans(scans: readonly OrderPickScanLine[]): BundleBinScanMap {
  const bins: Record<string, { sku: string; storageIdPrefix: string | null }> = {};
  for (const scan of scans) {
    if (!scan.sku) continue;
    bins[scan.lineId] = { sku: scan.sku, storageIdPrefix: scan.storageIdPrefix };
  }
  return bins;
}

function dropLocalScanSession(orderId: string): void {
  sessionStorage.removeItem(scanSessionStorageKey(orderId));
  sessionStorage.removeItem(scanSessionBinStorageKey(orderId));
}

/**
 * Shared scans win, then any lines still only in this tab are posted and the
 * session keys for this order are removed.
 */
export async function hydrateSharedOrderScans(
  orderId: string,
  items: readonly OrderItem[],
): Promise<{ items: OrderItem[]; bins: BundleBinScanMap }> {
  const remote = await fetchOrderPickScans(orderId);
  const sessionItems = loadScannedItems(orderId, items);
  const sessionBins = loadBundleBinScans(orderId);
  const remoteIds = new Set(remote.map((scan) => scan.lineId));
  const upload: OrderPickScanLine[] = [];
  for (const item of sessionItems) {
    if (!item.scanned || remoteIds.has(item.id)) continue;
    const bin = sessionBins[item.id];
    upload.push({
      lineId: item.id,
      sku: bin?.sku ?? null,
      storageIdPrefix: bin?.storageIdPrefix ?? null,
    });
  }
  if (upload.length > 0) {
    await saveOrderPickScans(orderId, upload);
  }
  dropLocalScanSession(orderId);
  const scans = upload.length > 0 ? [...remote, ...upload] : remote;
  const scannedIds = new Set(scans.map((scan) => scan.lineId));
  return {
    items: items.map((item) => ({ ...item, scanned: scannedIds.has(item.id) })),
    bins: binsFromOrderPickScans(scans),
  };
}
